import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Vendor, Vendors } from '@cdw-ae/common-data';
import {
  CdwDropdownMenuOption,
} from '@cdw-ae/common-ui-components-dropdown-menu';
import {
  CustomerMetadata,
  selectCustomerSettings,
} from '@cdw-ae/customer-lookup';
import type {
  SelectedFilterField,
} from '@cdw-ae/filter-bar-interfaces';
import { FilterBarParamStrategy } from '@cdw-ae/url-params';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import {
  distinctUntilChanged,
  finalize,
  Observable,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import * as FilterActions from '../../../../+state/filter/filter.actions';
import { selectSelectedFilters } from '../../../../+state/filter/filter.selectors';
import { RootState } from '../../../../common/interfaces/rootState.interface';
import { DevicesDataService } from '../../../../common/services/device/devices-data.service';
import { FilterBarId } from '../../../../common/services/route-config/models/route-config.model';
import { ChartActionTrigger } from '../../../../components/charts/models/chart.model';
import {
  DevicesBatteryHealthSummary as DeviceHealthSummary,
  DevicesAutoUpdateExpirySummary,
  DevicesLastActiveSummary,
  DevicesVersionSummary,
} from './dashboard.model';
import { DashboardContentComponent } from './dashboard-content/dashboard-content.component';
import {
  buildDefaultActiveDeviceFilter,
  DashboardCardAction,
  DASHBOARD_FILTER_BAR_SETTINGS,
} from './dashboard-content/dashboard-content.model';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  imports: [
    CommonModule,
    DashboardContentComponent,
  ],
})
export class DashboardComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  public readonly dashboardFilterBarId: FilterBarId = FilterBarId.Dashboard;
  public readonly deviceSearchFilterBarId: FilterBarId =
    FilterBarId.DeviceSearch;
  public isDevicesBatteryHealthSummaryDataLoading = true;
  public isDevicesVersionSummaryDataLoading = true;
  public isDevicesLastActiveSummaryDataLoading = true;
  public isDevicesAueSummaryDataLoading = true;
  public latestOsVersions: Record<Vendor, number> = {
    [Vendors.GOOGLE]: 0,
    [Vendors.MICROSOFT]: 0,
    [Vendors.APPLE]: 0,
  };
  private store: Store<RootState> = inject(Store);
  customerData$: Observable<CustomerMetadata | null> = this.store.select(
    selectCustomerSettings
  );

  // Filter bar properties
  public selectedFilters$: Observable<SelectedFilterField[]>;
  public currentFilters: SelectedFilterField[] = [];

  // dashboard data
  public dashboardData: {
    batteryHealthSummary: DeviceHealthSummary | null;
    deviceVersionSummary: DevicesVersionSummary | null;
    deviceLastActiveSummary: DevicesLastActiveSummary | null;
    deviceAueSummary: DevicesAutoUpdateExpirySummary | null;
  } = {
    batteryHealthSummary: null,
    deviceVersionSummary: null,
    deviceLastActiveSummary: null,
    deviceAueSummary: null,
  };

  // send action event to chart
  chartActionTrigger = new Subject<ChartActionTrigger>();

  private filterBarParamStrategy = inject(FilterBarParamStrategy);

  constructor(
    private devicesDataService: DevicesDataService,
  ) {
    this.selectedFilters$ = this.store.select(
      selectSelectedFilters(this.dashboardFilterBarId)
    );
  }

  ngOnInit(): void {
    // Initialize default ACTIVE status filter if no filters exist
    this.selectedFilters$.pipe(take(1)).subscribe((existingFilters) => {
      if (!existingFilters || existingFilters.length === 0) {
        // Build default ACTIVE filter using shared helper
        const defaultFilters = buildDefaultActiveDeviceFilter(
          this.filterBarParamStrategy,
          DASHBOARD_FILTER_BAR_SETTINGS
        );

        // Dispatch default filter to NgRx store to display in UI
        this.store.dispatch(
          FilterActions.updateFilters({
            filters: defaultFilters,
            filterBarId: this.dashboardFilterBarId,
          })
        );
      }
    });

    // Subscribe to filter changes from store (for initial load and URL-driven updates)
    this.selectedFilters$
      .pipe(
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        takeUntil(this.destroy$)
      )
      .subscribe((filters) => {
        this.currentFilters = filters;
        // Refresh data when filters change from store (URL updates, navigation, etc.)
        this.refreshData();
      });

    this.devicesDataService
      .getLatestOsVersions()
      .pipe(take(1))
      .subscribe((response) => {
        this.latestOsVersions = response || this.latestOsVersions;
        this.refreshData();
      });

    this.customerData$.pipe(take(1)).subscribe((customerData) => {
      // TODO: This should be using a cdw customer id not a google customer id. What happens when we have a microsoft only customer?
      const customerId = customerData?.googleCustomerId;

      // Refresh data if customer ID exists
      if (customerId) {
        this.refreshData();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onActionClick(action: DashboardCardAction): void {
    // Dispatch updateFilters action with current dashboard filters
    // This allows navigation to carry over the current filter state
    this.store.dispatch(
      FilterActions.updateFilters({
        identifier: action.cardId,
        clickedValue: action.clickedValue,
        filterBarId: action.targetFilterBar,
        filters: this.currentFilters,
      })
    );
  }

  public onAggregateReportSelect(option: CdwDropdownMenuOption): void {
    if (option.value) {
      this.onActionClick({ cardId: option.value, clickedValue: 'default' });
    }
  }

  public onDashboardFiltersChanged(filters: SelectedFilterField[]): void {
    // Set loading states immediately to destroy charts
    // charts will be recreated when loading finishes via store subscription

    this.isDevicesBatteryHealthSummaryDataLoading = true;
    this.isDevicesVersionSummaryDataLoading = true;
    this.isDevicesLastActiveSummaryDataLoading = true;
    this.isDevicesAueSummaryDataLoading = true;

    // Dispatch to NgRx store for URL/state management
    // The selectedFilters$ subscription will receive the update and call refreshData()
    this.store.dispatch(
      FilterActions.updateFilters({
        filters,
        filterBarId: this.dashboardFilterBarId,
      })
    );
  }

  private refreshData(): void {
    // Don't refresh if OS versions haven't loaded yet
    if (!this.latestOsVersions[Vendors.GOOGLE]) {
      return;
    }

    // Always use current filters from the filter bar
    // Default ACTIVE filter is initialized in ngOnInit if no filters exist
    const apiFilters = this.currentFilters || [];

    this.devicesDataService
      .getDeviceStateAnalysis(apiFilters)
      .pipe(
        take(1),
        finalize(() => {
          this.isDevicesBatteryHealthSummaryDataLoading = false;
          this.isDevicesVersionSummaryDataLoading = false;
          this.isDevicesLastActiveSummaryDataLoading = false;
          this.isDevicesAueSummaryDataLoading = false;
        })
      )
      .subscribe((deviceStateAnalysisResponse) => {
        if (deviceStateAnalysisResponse?.success) {
          const devicesSummary =
            this.devicesDataService.getDevicesHealthSummary(
              deviceStateAnalysisResponse.data,
              this.latestOsVersions
            );

          if (devicesSummary) {
            this.dashboardData = {
              batteryHealthSummary:
                devicesSummary.devicesbatteryHealthSummary || null,
              deviceVersionSummary:
                devicesSummary.devicesVersionSummary || null,
              deviceLastActiveSummary:
                devicesSummary.devicesLastActiveSummary || null,
              deviceAueSummary: devicesSummary.devicesAueSummary || null,
            };
          }
        }
      });
  }
}
